import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, InfoSheetPanel, Paragraph, Row, Separator, Toggle } from '@fs/zion-ui'
import FilterInput from '../../FilterInput'
import PersonsByImageCheckboxes from '../shared/personSelection/PersonsByImageCheckboxes'
import { usePersonsByImage } from '../shared/personSelection/usePersonsByImage'
import { useR2DispatchContext, useR2StateContext } from '../../../../providers/r2Provider/R2Context'

/**
 * The Add Existing Person sheet.
 *
 * Adds names that already exist on the document to a group. The toggle drives the R2 viewer's own
 * split screen, so the sheet lists the image being edited and, once the reference viewer is showing
 * one, the image beside it — each under its own heading.
 *
 * @param {Object} params
 * @param {Object} params.overlay - `useOverlay()` object controlling the sheet
 * @param {string} [params.focusPersonName] - Name of the focus person whose panel this was opened from;
 *   omitted when opened from New group, which falls back to a "New group" subtitle
 * @param {string} [params.focusPersonId] - The focus person, who never appears as a selectable row
 * @param {Array<string>} [params.groupMemberIds] - Person ids already in the group, shown ticked and locked
 * @param {import('react').RefObject<HTMLElement>} [params.confirmButtonRef] - Attached to Add, so a
 *   confirmation step raised from here can hand focus back to it
 * @param {(personIds: Array<string>) => void} [params.onConfirm] - Called with the selected person ids.
 *   Closing the sheet afterwards is the caller's decision, not this component's
 * @param {() => void} [params.onCancel] - Called when the patron backs out
 * @returns {React.ReactElement}
 */
const AddExistingPersonPanel = ({
  overlay,
  focusPersonName,
  focusPersonId,
  groupMemberIds = [],
  confirmButtonRef,
  onConfirm = () => {},
  onCancel = () => {},
}) => {
  const [t] = useTranslation()
  const r2State = useR2StateContext()
  const r2Dispatch = useR2DispatchContext()
  const [filterValue, setFilterValue] = useState('')
  const [selectedPersonIds, setSelectedPersonIds] = useState([])

  // Current members are shown ticked but kept out of `selectedPersonIds`, which is the move payload:
  // the sheet only ever adds.
  const checkedPersonIds = useMemo(
    () => [...new Set([...selectedPersonIds, ...groupMemberIds])],
    [selectedPersonIds, groupMemberIds]
  )

  // The viewed person does not appear as a selectable row in their own group.
  const excludePersonIds = useMemo(() => (focusPersonId ? [focusPersonId] : []), [focusPersonId])
  const { imageGroups } = usePersonsByImage({ excludePersonIds })

  // The sheet stays mounted between openings, so it would otherwise reopen on the last run's answers.
  const isOpen = overlay?.isOpen
  useEffect(() => {
    if (isOpen) return
    setSelectedPersonIds([])
    setFilterValue('')
  }, [isOpen])

  const subtitle = focusPersonName
    ? t('add-existing-person.subtitle', "{focusPersonName}'s group", { focusPersonName })
    : t('add-existing-person.subtitle.new-group', 'New group')

  const togglePerson = (personId) => {
    setSelectedPersonIds((current) =>
      current.includes(personId) ? current.filter((id) => id !== personId) : [...current, personId]
    )
  }

  const handleCancel = () => {
    onCancel()
    overlay.close()
  }

  // Does not close the sheet: only the caller knows whether a confirmation step follows, and those
  // stack on top of a sheet the patron has to be able to go back to.
  const handleConfirm = () => {
    onConfirm(selectedPersonIds)
  }

  return (
    <InfoSheetPanel
      {...overlay}
      title={t('add-existing-person.title', 'Add Existing Person')}
      subtitle={subtitle}
      closeLabel={t('add-existing-person.close.label', 'Close Add Existing Person')}
      headingColor="green"
      footer={
        <Row alignX="end" gutters="md">
          <Button
            emphasis="low"
            onClick={handleCancel}
            linkName="Add Existing Person Cancel"
            data-testid="add-existing-person-cancel-button"
          >
            {t('add-existing-person.cancel.button', 'Cancel')}
          </Button>
          <Button
            ref={confirmButtonRef}
            emphasis="high"
            disabled={selectedPersonIds.length === 0}
            onClick={handleConfirm}
            linkName="Add Existing Person Add"
            data-testid="add-existing-person-add-button"
          >
            {selectedPersonIds.length === 0
              ? t('add-existing-person.add.button', 'Add')
              : t('add-existing-person.add-with-count.button', 'Add ({selectionCount})', {
                  selectionCount: selectedPersonIds.length,
                })}
          </Button>
        </Row>
      }
    >
      {(overlay?.isOpen || overlay?.transitioning) && (
        <>
          <Paragraph size="sm">
            {t('add-existing-person.split-screen.instructions', 'To view names on other images, enable split screen.')}
          </Paragraph>
          <Separator size="xxs" />
          <Toggle
            allowLabelWrapping
            label={t('add-existing-person.split-screen.toggle', 'Enable Split Screen')}
            checked={r2State.splitScreen}
            onChange={({ target: { checked } }) => r2Dispatch({ type: 'SET_SPLIT_SCREEN', value: checked })}
            data-testid="add-existing-person-split-screen-toggle"
          />
          <Separator size="xs" />
          <FilterInput filterValue={filterValue} onFilterChange={setFilterValue} textFieldProps={{ dense: true }} />
          <Separator />
          <PersonsByImageCheckboxes
            imageGroups={imageGroups}
            selectedPersonIds={checkedPersonIds}
            disabledPersonIds={groupMemberIds}
            filterValue={filterValue}
            onToggle={togglePerson}
          />
        </>
      )}
    </InfoSheetPanel>
  )
}

export default AddExistingPersonPanel
